/**
 * Giveaway Ninja – Headless Web Component
 * =========================================
 * Data & logic only. Zero UI. You build the front-end.
 *
 * Usage:
 *   <gn-giveaway
 *     shop-id="boutique-velo-zone.myshopify.com"
 *     giveaway-id="698e079de70dbb1960109a10"
 *     id="gn">
 *   </gn-giveaway>
 *
 * Properties (read after 'gn:ready'):
 *   gn.loggedIn        – boolean
 *   gn.entries          – number (user's ticket count)
 *   gn.referralId       – string
 *   gn.referralUrl      – string (full share URL)
 *   gn.actions          – array  (bonus actions with .done flag)
 *   gn.campaign         – object (raw campaign data)
 *   gn.endDateMs        – number (end date timestamp in ms)
 *   gn.token            – string (current access token)
 *
 * Methods:
 *   await gn.signup(email, name)  → { success, entries, error }
 *   await gn.doAction(actionId)   → { success, entries, error }
 *   await gn.logout()             → void
 *   await gn.refresh()            → void (re-fetch state)
 *
 * Events (on the element):
 *   'gn:ready'   – { loggedIn, entries, actions }
 *   'gn:signup'  – { entries, referralId }
 *   'gn:action'  – { actionId, entries }
 *   'gn:logout'  – {}
 *   'gn:error'   – { method, message }
 */

/* =============================================================
   API INTERNALS
============================================================= */
const GN_API = "https://api.giveaway.ninja/api";
const GN_TK = "giveawayninja_at";

function _enc(v) {
  return encodeURIComponent(v);
}
function _body(o) {
  return "=" + _enc(JSON.stringify(o));
}

async function _parse(res) {
  const raw = await res.text();
  const first = JSON.parse(raw);
  return typeof first === "string" ? JSON.parse(first) : first;
}

function _readToken() {
  try {
    const t = localStorage.getItem(GN_TK);
    return t && t !== "null" ? t : "";
  } catch (_) {
    return "";
  }
}
function _saveToken(t) {
  try {
    localStorage.setItem(GN_TK, t);
  } catch (_) {}
}
function _clearToken() {
  try {
    localStorage.removeItem(GN_TK);
  } catch (_) {}
}

async function _auth(shopId) {
  const res = await fetch(`${GN_API}/token/auth`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: _body({ sid: shopId }),
  });
  let t = await res.text();
  t = t.replace(/^"|"$/g, "");
  _saveToken(t);
  return t;
}

async function _check(shopId, gwId, token) {
  return _parse(
    await fetch(
      `${GN_API}/giveaway/check?sid=${_enc(shopId)}&access_token=${_enc(token)}&id=${_enc(gwId)}&url=${_enc(location.href)}&cou=`,
    ),
  );
}

async function _info(gwId, token) {
  return _parse(
    await fetch(
      `${GN_API}/giveaway/info?id=${_enc(gwId)}&access_token=${_enc(token)}&tz=`,
    ),
  );
}

async function _signup(token, email, name, gwId) {
  return _parse(
    await fetch(`${GN_API}/giveaway/signupv2?access_token=${_enc(token)}`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: _body({
        lm: "email",
        e: email,
        n: name,
        p: "",
        o: false,
        oa: "",
        op: 0,
        rfr: "",
        gid: gwId,
        r: document.referrer || "",
        lq: location.href,
        co: "ca",
        pin: "",
        access_token: token,
        rtoken: "",
      }),
    }),
  );
}

async function _doAction(gwId, actionId, token, value = "") {
  return _parse(
    await fetch(
      `${GN_API}/giveaway/DoAction?g=${_enc(gwId)}&a=${_enc(actionId)}&access_token=${_enc(token)}&v=${_enc(value)}&c=&rtoken=`,
    ),
  );
}

/* =============================================================
   <gn-giveaway>
============================================================= */
class GnGiveaway extends HTMLElement {
  get shopId() {
    return this.getAttribute("shop-id") || "";
  }
  get giveawayId() {
    return this.getAttribute("giveaway-id") || "";
  }

  loggedIn = false;
  entries = 0;
  referralId = "";
  referralUrl = "";
  actions = [];
  campaign = null;
  endDateMs = 0;
  token = "";

  connectedCallback() {
    this._init();
  }

  _emit(name, detail = {}) {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  }

  /* ---- Init ---- */
  async _init() {
    try {
      this.token = _readToken();

      if (!this.token) {
        this.token = await _auth(this.shopId);
        this._emit("gn:ready", { loggedIn: false, entries: 0, actions: [] });
        return;
      }

      const info = await _info(this.giveawayId, this.token);

      if (info && info.UserLogged) {
        this._apply(info);
        this._emit("gn:ready", {
          loggedIn: true,
          entries: this.entries,
          actions: this.actions,
        });
      } else {
        this._emit("gn:ready", { loggedIn: false, entries: 0, actions: [] });
      }
    } catch (e) {
      console.warn("GN init error:", e);
      try {
        this.token = await _auth(this.shopId);
      } catch (_) {}
      this._emit("gn:ready", { loggedIn: false, entries: 0, actions: [] });
    }
  }

  /* ---- Apply API info to public properties ---- */
  _apply(info) {
    const gw = info.Giveaway || {};
    const performed = info.ActionsPerformed || [];
    const rawActions = gw.enac || gw.EntryActions || [];

    this.loggedIn = true;
    this.entries = info.UserEntries || 0;
    this.referralId = info.MyReferralId || "";
    this.endDateMs = parseInt(info.EndDateJS, 10) || 0;
    this.campaign = gw;
    this.token = _readToken();

    const refAction = rawActions.find((a) => a.Type === "refer-friend");
    if (refAction && this.referralId) {
      const base =
        refAction.Validation && refAction.Validation !== "-"
          ? refAction.Validation
          : location.origin;
      this.referralUrl = base + "?njref=" + this.referralId;
    }

    this.actions = rawActions.map((a) => {
      const p = performed.find((x) => x.Id === a.Id);
      const status = p ? p.Status : -1;
      return {
        ...a,
        done: status === -3 || status >= 1,
        status,
        earnedEntries: p ? p.Entries : 0,
      };
    });
  }

  /* ==========================
     PUBLIC: signup
  ========================== */
  async signup(email, name) {
    try {
      if (!this.token) this.token = await _auth(this.shopId);

      const campaign = await _check(this.shopId, this.giveawayId, this.token);
      const gwId =
        campaign?.Id && campaign.Id !== "no-giveaways"
          ? campaign.Id
          : this.giveawayId;

      const res = await _signup(this.token, email, name, gwId);

      if (res.result === "error") {
        this._emit("gn:error", { method: "signup", message: res.message });
        return { success: false, entries: 0, error: res.message };
      }
      if (res.result === "wrong-recaptcha") {
        this._emit("gn:error", {
          method: "signup",
          message: "wrong-recaptcha",
        });
        return { success: false, entries: 0, error: "wrong-recaptcha" };
      }

      if (res.access_token) {
        this.token = res.access_token;
        _saveToken(res.access_token);
      }

      this._apply({
        UserLogged: true,
        UserEntries: res.info?.UserEntries || 0,
        MyReferralId: res.referral_id || "",
        ActionsPerformed: res.info?.ActionsPerformed || [],
        Giveaway: res.info?.Giveaway || null,
        EndDateJS: res.info?.Giveaway?.EndDateJS || "0",
      });

      this._emit("gn:signup", {
        entries: this.entries,
        referralId: this.referralId,
      });
      return { success: true, entries: this.entries, error: null };
    } catch (e) {
      this._emit("gn:error", { method: "signup", message: e.message });
      return { success: false, entries: 0, error: e.message };
    }
  }

  /* ==========================
     PUBLIC: doAction
  ========================== */
  async doAction(actionId, value = "") {
    const gwId = this.campaign?.Id || this.giveawayId;
    try {
      const res = await _doAction(gwId, actionId, this.token, value);

      if (res.result === "ok") {
        this.entries = res.user_entries || this.entries;
        const action = this.actions.find((a) => a.Id === actionId);
        if (action) action.done = true;
        this._emit("gn:action", { actionId, entries: this.entries });
        return { success: true, entries: this.entries, error: null };
      }

      this._emit("gn:error", {
        method: "doAction",
        message: res.reason || "unknown",
      });
      return {
        success: false,
        entries: this.entries,
        error: res.reason || "unknown",
      };
    } catch (e) {
      this._emit("gn:error", { method: "doAction", message: e.message });
      return { success: false, entries: this.entries, error: e.message };
    }
  }

  /* ==========================
     PUBLIC: logout
  ========================== */
  async logout() {
    _clearToken();
    this.loggedIn = false;
    this.entries = 0;
    this.referralId = "";
    this.referralUrl = "";
    this.actions = [];
    this.campaign = null;
    try {
      this.token = await _auth(this.shopId);
    } catch (_) {}
    this._emit("gn:logout");
  }

  /* ==========================
     PUBLIC: refresh
  ========================== */
  async refresh() {
    if (!this.token) return;
    try {
      const info = await _info(this.giveawayId, this.token);
      if (info?.UserLogged) {
        this._apply(info);
        this._emit("gn:ready", {
          loggedIn: true,
          entries: this.entries,
          actions: this.actions,
        });
      }
    } catch (e) {
      this._emit("gn:error", { method: "refresh", message: e.message });
    }
  }
}

customElements.define("gn-giveaway", GnGiveaway);
